import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Theme,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { fetchApi, ApiResponse } from '../services/api';
import { format } from 'date-fns';
import SideMenu from '../components/SideMenu';
import ConfirmDialog from '../components/ConfirmDialog';
import AlertSnackbar from '../components/AlertSnackbar';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { alpha } from '@mui/material/styles';

interface ApiTableResponse {
  table: Itinerary[];
}

type Itinerary = {
  일정번호: number;
  일정명: string;
  상품가: number;
  유저명: string;
  등록일시: string;
  공유: boolean;
  엑셀파일경로: string;
  내용: string;
  즐겨찾기: boolean;
} & {
  [key: string]: string | number | boolean;
};

const drawerWidth = 240;

const ItineraryListPage: React.FC = () => {
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItinerary, setSelectedItinerary] = useState<Itinerary | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [popupContent, setPopupContent] = useState<any | null>(null);
  const [filter, setFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: () => { } });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClose = () => setOpen(false);

  const fetchItineraries = async (keyword?: string) => {
    try {
      const CompanyNo = localStorage.getItem('CompanyNo');
      if (!CompanyNo) {
        throw new Error('Company ID not found');
      }

      // DB에 일정 정보 저장
      const procedureName = 'ITN_S_간편일정휴지통';
      const postData = [
        { "컬럼명": "@업체번호", "값": CompanyNo },
      ];

      // 검색어가 있는 경우에만 추가
      if (keyword) {
        postData.push({ "컬럼명": "@검색어", "값": keyword });
      }

      const response = await fetchApi<ApiTableResponse>(procedureName, postData);

      if (response.data && response.data.table) {
        setItineraries(response.data.table);

      } else {
        setItineraries([]);
      }
    } catch (error) {
      console.error('Failed to fetch itineraries:', error);
      setItineraries([]);
    }
  };

  useEffect(() => {
    fetchItineraries();
  }, []);

  const handleSearch = () => {
    fetchItineraries(searchKeyword);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  const handleOpen = async (itinerary: Itinerary) => {
    try {
      const postData = [
        { "컬럼명": "@일정번호", "값": itinerary.일정번호.toString() },
        { "컬럼명": "@공유여부", "값": 0 }   // NOT NULL 일때 조회수 + 1 
      ];

      const response = await fetchApi<ApiTableResponse>("ITN_S_일정표", postData);

      if (response.data && response.data.table) {
        setPopupContent(response.data.table[0]);
      }

      setOpen(true);
    } catch (error) {
      console.error('Failed to fetch itinerary detail:', error);
      setPopupContent(itinerary);
      setOpen(true);
    }
  };
  const decodeToken = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const handleDelete = async (itinerary: Itinerary) => {
    const procedureName = "ITN_T_간편일정삭제";
    const token = localStorage.getItem('token');
    const decodedToken = token ? decodeToken(token) : {};
    const postData = [
      { "컬럼명": "@일정번호", "값": itinerary.일정번호 },
      // { "컬럼명": "@유저번호", "값": decodedToken.UserNo.toString() },
      { "컬럼명": "@업체번호", "값": decodedToken.CompanyNo.toString() },
    ];
    try {
      await fetchApi(procedureName, postData);
      setItineraries(prevItineraries => prevItineraries.filter(item => item.일정번호 !== itinerary.일정번호));
    } catch (error) {
      console.error("Error deleting itinerary:", error);
    }
  };

  const handleDeleteConfirmation = (itinerary: Itinerary) => {
    setConfirmDialog({
      open: true,
      message: '일정표를 완전히 삭제할까요?',
      onConfirm: async () => {
        try {
          await handleDelete(itinerary);
          setSnackbar({
            open: true,
            message: '일정표가 삭제되었습니다.',
            severity: 'success'
          });
        } catch (error) {
          setSnackbar({
            open: true,
            message: '삭제 중 오류가 발생했습니다.',
            severity: 'error'
          });
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handleView = (itinerary: Itinerary) => {
    setSelectedItinerary(itinerary);
    setOpenDialog(true);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch {
      return dateString;
    }
  };

  const handleSort = (key: string) => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedItineraries = React.useMemo(() => {
    let sortableItems = [...itineraries];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [itineraries, sortConfig]);

  const filteredItineraries = sortedItineraries.filter(itinerary =>
    Object.values(itinerary).some(value =>
      value !== null && value.toString().toLowerCase().includes(filter.toLowerCase())
    )
  );

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // Utility function to truncate text in the middle with ellipsis
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    const half = Math.floor(maxLength / 2);
    return text.slice(0, half) + '...' + text.slice(-half);
  };

  const handleRestore = async (itinerary: Itinerary) => {
    setConfirmDialog({
      open: true,
      message: '일정표를 다시 복원할까요?',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const decodedToken = token ? decodeToken(token) : {};
          const postData = [
            { "컬럼명": "@일정번호", "값": itinerary.일정번호.toString() },
            { "컬럼명": "@즐겨찾기", "값": 0 }
          ];

          await fetchApi("ITN_T_간편일정즐겨찾기", postData);
          setItineraries(prevItineraries => prevItineraries.filter(item => item.일정번호 !== itinerary.일정번호));
          setSnackbar({
            open: true,
            message: '일정표가 복원되었습니다.',
            severity: 'success'
          });
        } catch (error) {
          console.error('Failed to restore itinerary:', error);
          setSnackbar({
            open: true,
            message: '복원 중 오류가 발생했습니다.',
            severity: 'error'
          });
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <SideMenu
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      {/* {companyNo && <MenuTree 업체번호={parseInt(companyNo, 10)} />} */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          p: 0,
          display: 'flex',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <ConfirmDialog
            open={confirmDialog.open}
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          />
          <AlertSnackbar
            open={snackbar.open}
            message={snackbar.message}
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          />
          <Paper
            elevation={0}
            sx={{
              ...(isMobile ? { p: 1.5, mt: 6 } : { py: 2.1, px: 1.6, m: 1.5, mt: 0 }),
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              height: '62px',
            }}
          >
            <Box sx={{ mb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DeleteOutlineIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {isMobile ? '휴지통' : '휴지통'}
                </Typography>
              </Box>

            </Box>
          </Paper>
          {isMobile ? (
            <Grid container spacing={1} sx={{ mt: 0.1 }}>
              {filteredItineraries.map((itinerary) => (
                <Grid item xs={12} key={itinerary.일정번호}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Box onClick={() => handleOpen(itinerary)}
                      sx={{ position: 'relative', p: 2, pb: 1 }}>
                      <Typography variant="h6"
                        /*component="div"*/
                        sx={{ fontWeight: 600, fontSize: '1.0rem', color: '#1a1a1a', mb: 1 }}>
                        {itinerary.일정명}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {/* <LocationOnOutlined sx={{ fontSize: '0.9rem', color: 'grey.600' }} /> */}
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}>
                            {(itinerary.방문도시 as string).split(',').map((city: string, index: number, array: string[]) => {
                              if (index < 4 || (index === 4 && array.length <= 5)) {
                                return (
                                  <Box key={index} sx={{ border: '1px solid #e0e0e0', padding: '4px 6px', borderRadius: '4px', fontSize: '13px', height: '24px', backgroundColor: 'white', display: 'flex', alignItems: 'center' }}>
                                    {city.trim()}
                                  </Box>
                                );
                              } else if (index === 4) {
                                const remainingCount = array.length - 4;
                                return (
                                  <Box key={index} sx={{ border: '1px solid', padding: '4px 6px', borderRadius: '4px', fontSize: '13px', height: '24px', color: 'primary.main', borderColor: 'primary.main', backgroundColor: 'white', display: 'flex', alignItems: 'center' }}>
                                    +{remainingCount}
                                  </Box>
                                );
                              }
                              return null;
                            })}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {/* <PaidOutlined sx={{ fontSize: '0.9rem', color: 'grey.600' }} /> */}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            {itinerary.상품가}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <CardActions
                      sx={{ mt: 'auto', p: 2, pt: 1, display: 'flex', gap: 1, flexWrap: 'nowrap', borderTop: '1px solid', borderColor: 'grey.100', overflowX: 'auto' }}>

                      <Tooltip title="일정 상세보기" arrow>
                        <IconButton
                          onClick={() => handleOpen(itinerary)}
                          size="small"
                          sx={{
                            height: '41px',
                            width: '41px',
                            backgroundColor: 'white',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}>
                          <EventNoteIcon color='primary' sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="일정 복원하기" arrow>
                        <IconButton
                          onClick={(e) => handleRestore(itinerary)}
                          size="small"
                          sx={{
                            height: '41px',
                            width: '41px',
                            backgroundColor: 'white',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}>
                          <RestoreIcon color="primary" sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="일정 영구삭제" arrow>
                        <IconButton
                          onClick={() => handleDeleteConfirmation(itinerary)}
                          size="small"
                          sx={{
                            height: '41px',
                            width: '41px',
                            backgroundColor: 'white',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            }
                          }}>
                          <DeleteOutlineIcon color="primary" sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Tooltip>
                      <Box
                        sx={{
                          pr: 1,
                          textAlign: 'right',
                          width: '100%',
                          fontSize: '14px',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}>
                        {itinerary.원본일정번호 ? itinerary.업체명 : ''}
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                m: 1.5,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fff',
                minHeight: 'calc(100vh - 88px)',
              }}
            >
              <TableContainer component={Paper} elevation={0}>
                <Table
                  size="small"
                  aria-label="휴지통 일정 목록"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': {
                      padding: '5px',
                      borderBottom: '1px solid rgba(224, 224, 224, 1)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ height: '42.33px' }}>
                      <TableCell sx={{ cursor: 'pointer', width: '30%' }} onClick={() => handleSort('일정명')}>
                        일정명 {sortConfig?.key === '일정명' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ width: 450, flex: 'none', cursor: 'pointer' }} className="cities hidden-info" onClick={() => handleSort('방문도시')}>
                        방문도시{sortConfig?.key === '방문도시' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '15%' }} className="hidden-info" onClick={() => handleSort('상품가')}>
                        상품가 {sortConfig?.key === '상품가' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '15%' }} className="hidden-info" onClick={() => handleSort('등록')}>
                        등록 {sortConfig?.key === '등록' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '8%' }} className="hidden-info" onClick={() => handleSort('등록일시')}>
                        등록일시 {sortConfig?.key === '등록일시' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '5%' }} className="hidden-info" onClick={() => handleSort('공유')}>
                        공개 {sortConfig?.key === '공개' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '5%' }} className="hidden-info" onClick={() => handleSort('조회수')}>
                        조회수 {sortConfig?.key === '조회수' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', width: '5%' }} className="hidden-info" onClick={() => handleSort('공유수')}>
                        공유수 {sortConfig?.key === '공유수' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItineraries.map((itinerary) => (
                      <TableRow
                        key={itinerary.일정번호}
                        hover
                        sx={{
                          height: '52px',
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                            outline: (theme) => `1px solid ${theme.palette.primary.main}`,
                            '& .MuiTableCell-root': {
                              backgroundColor: 'transparent',
                              color: 'inherit',
                            },
                            '& .MuiIconButton-root': {
                              backgroundColor: 'white',
                              '&:hover': {
                                backgroundColor: 'grey.100',
                              }
                            },
                            '& .hidden-info': {
                              color: 'transparent',
                              '& > *': {
                                visibility: 'hidden',
                              },
                            },
                            '& .management-icons': {
                              display: 'flex',
                              visibility: 'visible',
                              height: '52px',
                              gap: '16px',
                            },
                            '& .cities': {
                              display: 'none',
                            },
                          },
                          '& .management-icons': {
                            display: 'none',
                            visibility: 'hidden',
                          }
                        }}
                      >
                        <TableCell onClick={() => handleOpen(itinerary)} style={{ cursor: 'pointer' }}>
                          {itinerary.일정명}
                        </TableCell>
                        <TableCell sx={{ display: 'flex', gap: '5px', height: '44px', mt: '8px' }} className="cities hidden-info">
                          {((itinerary.방문도시?.toString() || '') as string).split(',').filter(city => city.trim() !== '').map((city: string, index: number, array: string[]) => {
                            if (index < 4 || (index === 4 && array.length <= 5)) {
                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    border: '1px solid #e0e0e0',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    height: '24px',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {city.trim()}
                                </Box>
                              );
                            } else if (index === 4) {
                              const remainingCount = array.length - 4;
                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    border: '1px solid',
                                    padding: '4px 6px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    height: '24px',
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  +{remainingCount}
                                </Box>
                              );
                            }
                            return null;
                          })}
                        </TableCell>
                        <TableCell sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }} className="management-icons">
                          <Tooltip title="일정 상세보기" arrow>
                            <IconButton
                              onClick={() => handleOpen(itinerary)}
                              size="small"
                              sx={{
                                height: '41px',
                                width: '41px',
                                backgroundColor: 'white',
                                boxShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                transition: 'all 0.1s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'white !important',
                                  transform: 'translate(-0.5px, -0.5px)',
                                  boxShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                                }
                              }}>
                              <EventNoteIcon color='primary' sx={{ fontSize: '20px' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="일정 복원하기" arrow>
                            <IconButton
                              onClick={(e) => handleRestore(itinerary)}
                              size="small"
                              sx={{
                                height: '41px',
                                width: '41px',
                                backgroundColor: 'white',
                                boxShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                transition: 'all 0.1s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'white !important',
                                  transform: 'translate(-0.5px, -0.5px)',
                                  boxShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                                }
                              }}>
                              <RestoreIcon color="primary" sx={{ fontSize: '20px' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="일정 영구삭제" arrow>
                            <IconButton
                              onClick={() => handleDeleteConfirmation(itinerary)}
                              size="small"
                              sx={{
                                height: '41px',
                                width: '41px',
                                backgroundColor: 'white',
                                boxShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                transition: 'all 0.1s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'white !important',
                                  transform: 'translate(-0.5px, -0.5px)',
                                  boxShadow: '2px 2px 4px rgba(0,0,0,0.4)',
                                }
                              }}>
                              <DeleteOutlineIcon color="primary" sx={{ fontSize: '20px' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="hidden-info">{truncateText(itinerary.상품가.toString(), 20)}</TableCell>
                        <TableCell className="hidden-info">{itinerary.업체명} | {itinerary.유저명}</TableCell>
                        <TableCell className="hidden-info" sx={{ textAlign: 'center' }}>{formatDate(itinerary.등록일시)}</TableCell>
                        <TableCell className="hidden-info" sx={{ textAlign: 'center' }}>
                          <IconButton size="small" >
                            {itinerary.공유 === true ? (
                              <PublicOutlined sx={{ fontSize: '18px', color: 'grey.700' }} />
                            ) : (
                              <LockOutlined sx={{ fontSize: '18px', color: 'grey.700' }} />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell className="hidden-info" sx={{ textAlign: 'center' }}>{itinerary.조회수}</TableCell>
                        <TableCell className="hidden-info" sx={{ textAlign: 'center' }}>{itinerary.공유수}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Box>

      {/* 다이얼로그들 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        {selectedItinerary && (
          <>
            <DialogTitle>일정 상세 정보</DialogTitle>
            <DialogContent>
              <Typography variant="h6">{selectedItinerary.일정명}</Typography>
              <Typography>상품가: {selectedItinerary.상품가}</Typography>
              <Typography>등록자: {selectedItinerary.유저명}</Typography>
              <Typography>등록일시: {formatDate(selectedItinerary.등록일시)}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>닫기</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 다이얼로그들 */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        fullScreen={useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            // maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' },
            maxHeight: '100vh',
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
      >
        <Box sx={{ height: '95vh', position: 'relative', overflow: 'auto' }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.700',
              bgcolor: 'white',
              '&:hover': {
                bgcolor: 'grey.100'
              },
              zIndex: 1,
              display: { xs: 'flex', sm: 'none' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <iframe
            srcDoc={popupContent?.내용 || ''}
            style={{ width: '100%', height: `calc( 100% - 8px)`, border: 'none' }}
            title="일정 내용"
          />
        </Box>
      </Dialog>

    </Box>
  );
};

export default ItineraryListPage;
